import { BULK_UPLOAD_CONFIG } from './constants';
import { BulkUploadLogger } from './logging';

/**
 * Build query parameters for paginated requests
 */
export function buildPaginationParams(
  page: number,
  limit: number,
  status?: string
): { page: number; limit: number; status?: string } {
  const params: { page: number; limit: number; status?: string } = { page, limit };
  if (status) {
    params.status = status;
  }
  return params;
}

/**
 * Upload file to S3 with retry logic for transient failures
 */
export async function uploadToS3WithRetry(
  uploadUrl: string,
  file: File,
  maxRetries: number = BULK_UPLOAD_CONFIG.MAX_RETRY_ATTEMPTS
): Promise<void> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), BULK_UPLOAD_CONFIG.UPLOAD_TIMEOUT_MS);

    try {
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': 'application/zip',
        },
        signal: controller.signal,
      });

      if (!uploadResponse.ok) {
        throw new Error(
          `S3 upload failed with status ${uploadResponse.status}: ${uploadResponse.statusText}`
        );
      }

      // Upload successful - clear timeout before returning
      clearTimeout(timeoutId);
      return;
    } catch (err) {
      // Clear timeout immediately to prevent memory leaks
      clearTimeout(timeoutId);

      lastError = err instanceof Error ? err : new Error('Unknown error');

      // Check if error is retryable
      const isNetworkError = lastError.name === 'TypeError' || lastError.message.includes('Failed to fetch');
      const isTimeout = lastError.name === 'AbortError';
      const isRetryable = isNetworkError || isTimeout;

      if (!isRetryable || attempt === maxRetries - 1) {
        // Not retryable or last attempt - throw error
        if (isTimeout) {
          throw new Error(
            `Upload timed out after ${BULK_UPLOAD_CONFIG.UPLOAD_TIMEOUT_MINUTES} minutes. Please check your network connection and try again.`,
            { cause: err }
          );
        } else if (isNetworkError) {
          throw new Error(
            'Network error during upload. Please check your internet connection and try again.',
            { cause: err }
          );
        } else {
          throw lastError;
        }
      }

      // Wait before retry with exponential backoff
      const delayMs = Math.min(
        BULK_UPLOAD_CONFIG.INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt),
        BULK_UPLOAD_CONFIG.MAX_RETRY_DELAY_MS
      );
      BulkUploadLogger.retryAttempt(attempt + 1, lastError, { delayMs });
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  // Should never reach here, but just in case
  throw lastError || new Error('Upload failed after retries');
}

/**
 * Format file size in bytes to human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return '0 Bytes';
  }
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Validate file for upload
 */
export function validateUploadFile(file: File): { valid: boolean; error?: string } {
  if (!file.name.toLowerCase().endsWith(BULK_UPLOAD_CONFIG.ALLOWED_FILE_TYPE)) {
    return { valid: false, error: 'Only ZIP files are allowed' };
  }

  if (file.size > BULK_UPLOAD_CONFIG.MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size exceeds ${formatFileSize(BULK_UPLOAD_CONFIG.MAX_FILE_SIZE)} limit`,
    };
  }

  return { valid: true };
}

/**
 * Calculate exponential backoff interval
 */
export function calculateBackoffInterval(currentInterval: number, maxInterval: number): number {
  return Math.min(currentInterval * 1.5, maxInterval);
}

/**
 * Execute promises with concurrency limit
 * @param items - Array of items to process
 * @param limit - Maximum concurrent operations
 * @param fn - Async function to execute for each item
 * @returns Promise resolving to array of results
 */
export async function withConcurrencyLimit<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = [];
  const executing: Promise<void>[] = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const promise = fn(item, i).then((result) => {
      results[i] = result;
    });

    // Wrap promise to handle cleanup after completion
    const wrappedPromise = promise.finally(() => {
      // Remove from executing when done - use wrappedPromise reference to avoid race condition
      const index = executing.indexOf(wrappedPromise);
      if (index > -1) {
        executing.splice(index, 1);
      }
    });

    executing.push(wrappedPromise);

    if (executing.length >= limit) {
      await Promise.race(executing);
    }
  }

  await Promise.all(executing);
  return results;
}
